/* ============================================================================
   CART DRAWER (assets/cart-drawer.js) — prototype
   ----------------------------------------------------------------------------
   Zero mutation logic lives here. The stock theme.js Cart class keeps handling
   /cart/add.js + /cart/change.js, the header badge, and app events (Kite, XE,
   Candyrack, Intelligems). This script only:
     1. Opens/closes the drawer (toggle, overlay, Escape, focus management)
     2. Auto-opens on `product:added`
     3. Re-fetches its own section (?section_id=cart-drawer) whenever theme.js
        announces `cart:rerendered`, then re-emits `cart:rerendered` (bubbling)
        from the drawer for any app listeners.
   NOTE: theme.js dispatches `cart:rerendered` WITHOUT bubbles on the mini-cart
   path (on .header__action-item--cart), so we must listen there directly.
   ========================================================================== */
(function () {
  "use strict";

  function init() {
    var drawer = document.getElementById("cart-drawer");
    if (!drawer) return;

    var overlay = document.querySelector("[data-cart-drawer-overlay]");
    var headerCartEl = document.querySelector(".header__action-item--cart");
    var lastFocused = null;
    var refreshTimer = null;
    var animTimer = null;

    /* ---------- open / close ---------- */

    function setToggleState(expanded) {
      document.querySelectorAll("[data-cart-drawer-toggle]").forEach(function (t) {
        t.setAttribute("aria-expanded", expanded ? "true" : "false");
      });
    }

    function open() {
      if (drawer.getAttribute("aria-hidden") === "false") return;
      lastFocused = document.activeElement;
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(function () { overlay.setAttribute("data-visible", ""); });
      }
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("cart-drawer-open");
      setToggleState(true);
      var closeBtn = drawer.querySelector("[data-cart-drawer-close]");
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (drawer.getAttribute("aria-hidden") === "true") return;
      drawer.classList.add("is-animating");
      drawer.setAttribute("aria-hidden", "true");
      if (overlay) {
        overlay.removeAttribute("data-visible");
        setTimeout(function () { overlay.hidden = true; }, 300);
      }
      clearTimeout(animTimer);
      animTimer = setTimeout(function () { drawer.classList.remove("is-animating"); }, 350);
      document.body.classList.remove("cart-drawer-open");
      setToggleState(false);
      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    }

    document.addEventListener("click", function (event) {
      var toggle = event.target.closest("[data-cart-drawer-toggle]");
      if (toggle) {
        event.preventDefault();
        if (drawer.getAttribute("aria-hidden") === "false") { close(); } else { open(); }
        return;
      }
      if (event.target.closest("[data-cart-drawer-close]")) { event.preventDefault(); close(); }
    });

    if (overlay) overlay.addEventListener("click", close);

    document.addEventListener("keydown", function (event) {
      if (drawer.getAttribute("aria-hidden") === "true") return;
      if (event.key === "Escape") { close(); return; }
      if (event.key !== "Tab") return;
      /* light focus trap */
      var focusables = drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    /* ---------- upsells (Product Recommendations API, server-rendered) ---------- */

    /* power-customer dismissal: hide the rail for 14 days on both surfaces */
    var UPSELLS_DISMISS_KEY = "ep-cart-upsells-dismissed";
    var UPSELLS_DISMISS_TTL = 14 * 24 * 60 * 60 * 1000;

    function upsellsDismissed() {
      try {
        var ts = parseInt(localStorage.getItem(UPSELLS_DISMISS_KEY), 10);
        if (!ts) return false;
        if (Date.now() - ts > UPSELLS_DISMISS_TTL) {
          localStorage.removeItem(UPSELLS_DISMISS_KEY);
          return false;
        }
        return true;
      } catch (e) { return false; }
    }

    document.addEventListener("click", function (event) {
      if (!event.target.closest("[data-cart-upsells-dismiss]")) return;
      try { localStorage.setItem(UPSELLS_DISMISS_KEY, String(Date.now())); } catch (e) {}
      document.querySelectorAll("[data-cart-drawer-upsells], [data-cart-page-upsells]").forEach(function (slot) {
        slot.innerHTML = "";
      });
    });

    /* ---- money formatting for client-built cards (theme moneyFormat) ---- */
    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      var parts = amount.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      amount = parts.join(".");
      var fmt = (window.theme && window.theme.moneyFormat) || "${{amount}}";
      return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount).replace(/<[^>]+>/g, "");
    }

    /* Build the rail from Code Black Belt FBT recommendations (official
       public API — order-data-driven "frequently bought together" pairs,
       CORS-open). Returns a promise resolving true if the rail was filled. */
    function loadCbbUpsells(pid, slots) {
      var url = "https://cdn.codeblackbelt.com/public/api/v1/frequently-bought-together?productId=" +
        encodeURIComponent(pid) + "&shop=ep-prod.myshopify.com";
      return fetch(url)
        .then(function (res) { if (!res.ok) throw new Error("cbb " + res.status); return res.json(); })
        .then(function (data) {
          var recs = (data && data.recommendations) || [];
          if (!recs.length) return false;
          return fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
            var inCart = {};
            cart.items.forEach(function (i) { inCart[i.product_id] = true; });
            var picks = recs.filter(function (r) { return !inCart[r.id]; }).slice(0, 4);
            if (!picks.length) return false;
            return Promise.all(picks.map(function (r) {
              return fetch("/products/" + r.handle + ".js")
                .then(function (res) { return res.ok ? res.json() : null; })
                .catch(function () { return null; });
            })).then(function (products) {
              var cards = products.filter(function (p) { return p && p.available; }).map(function (p) {
                var v = p.variants[0];
                var singleVariant = p.variants.length === 1;
                var img = p.featured_image ? '<img src="' + p.featured_image + '&width=160" alt="">' : "";
                if (p.featured_image && p.featured_image.indexOf("?") === -1) {
                  img = '<img src="' + p.featured_image + '?width=160" alt="">';
                }
                var price = '<div class="cart-drawer__upsell-price">' +
                  (p.compare_at_price && p.compare_at_price > p.price
                    ? '<span class="cart-drawer__price--highlight">' + formatMoney(p.price) + "</span> <s>" + formatMoney(p.compare_at_price) + "</s>"
                    : formatMoney(p.price)) + "</div>";
                var action = singleVariant
                  ? '<button type="button" class="cart-drawer__upsell-add" data-cart-drawer-upsell-add data-variant-id="' + v.id + '">Add</button>'
                  : '<a href="' + p.url + '" class="cart-drawer__upsell-add cart-drawer__upsell-add--link">Options</a>';
                return '<div class="cart-drawer__upsell-card">' +
                  '<a href="' + p.url + '" class="cart-drawer__upsell-media" tabindex="-1" aria-hidden="true">' + img + "</a>" +
                  '<a href="' + p.url + '" class="cart-drawer__upsell-title">' + p.title.replace(/</g, "&lt;") + "</a>" +
                  price + action + "</div>";
              });
              if (!cards.length) return false;
              var html = '<div class="cart-drawer__upsells-inner">' +
                '<div class="cart-drawer__upsells-head">' +
                '<h3 class="cart-drawer__upsells-heading">Complete your setup</h3>' +
                '<button type="button" class="cart-drawer__upsells-dismiss" data-cart-upsells-dismiss aria-label="Hide suggestions">Hide ✕</button>' +
                "</div>" +
                '<div class="cart-drawer__upsell-row">' + cards.join("") + "</div></div>";
              slots.forEach(function (slot) { slot.innerHTML = html; });
              return true;
            });
          });
        });
    }

    function loadUpsells() {
      /* fills the drawer slot AND the cart-page slot. Source chain:
         1. Code Black Belt FBT public API — real co-purchase pairs
         2. Shopify intent=complementary (Search & Discovery curation)
         3. Shopify intent=related (algorithmic, never blank)  */
      var slots = document.querySelectorAll("[data-cart-drawer-upsells], [data-cart-page-upsells]");
      var pid = drawer.getAttribute("data-newest-product-id");
      if (!slots.length || !pid) return;
      if (upsellsDismissed()) {
        slots.forEach(function (slot) { slot.innerHTML = ""; });
        return;
      }
      var base = (window.routes && window.routes.rootUrlWithoutSlash ? window.routes.rootUrlWithoutSlash : "") +
        "/recommendations/products?section_id=cart-drawer-upsells&product_id=" + pid + "&limit=6";

      function extractInner(html) {
        var temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.querySelector(".cart-drawer__upsells-inner");
      }
      function fill(inner) {
        slots.forEach(function (slot) { slot.innerHTML = inner ? inner.outerHTML : ""; });
      }
      function shopifyChain() {
        return fetch(base + "&intent=complementary", { credentials: "same-origin" })
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var inner = extractInner(html);
            if (inner) { fill(inner); return null; }
            return fetch(base + "&intent=related", { credentials: "same-origin" })
              .then(function (res) { return res.text(); })
              .then(function (html2) { fill(extractInner(html2)); });
          });
      }

      loadCbbUpsells(pid, slots)
        .then(function (filled) { if (!filled) return shopifyChain(); })
        .catch(function () { return shopifyChain(); })
        .catch(function () { /* recommendations are progressive enhancement */ });
    }

    /* upsell one-tap add: POST /cart/add.js, then let the stock machinery react */
    document.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-cart-drawer-upsell-add]");
      if (!btn) return;
      event.preventDefault();
      btn.disabled = true;
      btn.textContent = "Adding…";
      fetch((window.routes && window.routes.cartAddUrl ? window.routes.cartAddUrl : "/cart/add") + ".js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute("data-variant-id"), 10), quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error("add failed");
          /* documentElement, not document — theme.js's Delegate listens there */
          document.documentElement.dispatchEvent(new CustomEvent("product:added", { bubbles: true, detail: { quantity: 1 } }));
        })
        .catch(function () { btn.disabled = false; btn.textContent = "Add"; });
    });

    /* ---------- equipment T&C gating ----------
       The Effective Apps embed injects an agreement checkbox for equipment
       carts and gates checkout on the LIVE cart via a cloned button — but it
       doesn't attach to the drawer, and AJAX re-renders replace our button,
       stripping any gating. Enforce it here for BOTH surfaces (the drawer
       and the redesigned cart page share the .cart-drawer__form class):
       block submit while unchecked + inline error beside the checkbox. */

    function findTcCheckbox(form) {
      var scope = form.closest("#cart-drawer") || form.closest(".cart-page");
      if (!scope) return null;
      /* our Liquid-rendered agreement first; app-injected row as fallback */
      return scope.querySelector("[data-cart-tc]") || scope.querySelector(
        ".cart-drawer__footer > :not(form) input[type=\"checkbox\"], .cart-drawer__form input[type=\"checkbox\"]"
      );
    }

    /* checked-state persistence: Liquid re-renders the row unchecked on every
       cart mutation; remember the agreement for the browsing session */
    var TC_KEY = "ep-equipment-tc-agreed";
    function restoreTcState() {
      var agreed = false;
      try { agreed = sessionStorage.getItem(TC_KEY) === "1"; } catch (e) {}
      if (!agreed) return;
      document.querySelectorAll("[data-cart-tc]").forEach(function (b) { b.checked = true; });
    }
    document.addEventListener("change", function (event) {
      if (event.target.matches && event.target.matches("[data-cart-tc]")) {
        try {
          if (event.target.checked) sessionStorage.setItem(TC_KEY, "1");
          else sessionStorage.removeItem(TC_KEY);
        } catch (e) {}
        /* mirror across surfaces (drawer + cart page both render the row) */
        document.querySelectorAll("[data-cart-tc]").forEach(function (b) { b.checked = event.target.checked; });
      }
    });
    restoreTcState();

    function clearTcError(box) {
      var wrapper = box.closest("p, div, label") || box.parentElement;
      wrapper.classList.remove("cart-drawer__tc-invalid");
      var err = wrapper.parentElement.querySelector(".cart-drawer__tc-error");
      if (err) err.remove();
    }

    function showTcError(box) {
      clearTcError(box);
      var wrapper = box.closest("p, div, label") || box.parentElement;
      wrapper.classList.add("cart-drawer__tc-invalid");
      var err = document.createElement("span");
      err.className = "cart-drawer__tc-error";
      err.setAttribute("role", "alert");
      err.textContent = window.EFFECTIVE_APPS_TAC_ALERT ||
        "Please agree to the equipment terms and conditions to continue.";
      wrapper.insertAdjacentElement("afterend", err);
      wrapper.scrollIntoView({ block: "center", behavior: "smooth" });
      box.focus();
    }

    document.addEventListener("submit", function (event) {
      var form = event.target.closest ? event.target.closest(".cart-drawer__form") : null;
      if (!form && event.target.classList && event.target.classList.contains("cart-drawer__form")) form = event.target;
      if (!form) return;
      var box = findTcCheckbox(form);
      if (!box || box.checked) return;
      event.preventDefault();
      event.stopPropagation();
      showTcError(box);
    }, true);

    document.addEventListener("change", function (event) {
      if (event.target.matches && event.target.matches("input[type=\"checkbox\"]") && event.target.checked) {
        var inCartUi = event.target.closest("#cart-drawer, .cart-page");
        if (inCartUi) clearTcError(event.target);
      }
    });

    /* ---------- refresh (parasitic: theme.js announces, we re-render) ---------- */

    /* Apply a cart-drawer render (page fetch OR bundled section HTML from a
       cart-perf.js mutation response — both contain #cart-drawer) with the
       full pipeline: app-node preservation, scroll keep, T&C restore,
       upsells, and the cart:rerendered re-announce for apps. */
    function applyDrawerHtml(html) {
          var temp = document.createElement("div");
          temp.innerHTML = html;
          var fresh = temp.querySelector("#cart-drawer");
          if (!fresh) return;

          var itemsEl = drawer.querySelector("[data-cart-drawer-items]");
          var scrollPos = itemsEl ? itemsEl.scrollTop : 0;

          /* Preserve app-injected nodes (e.g. Effective Apps equipment T&C —
             observed injecting a bare <p> directly into the FOOTER, not the
             form) — apps inject once on page load and would be wiped by the
             innerHTML swap. Moving nodes keeps their listeners. */
          function collectInjected(container, extraExcludes) {
            if (!container) return [];
            return Array.prototype.slice.call(container.children).filter(function (el) {
              var cls = String(el.className || "");
              if (cls.indexOf("cart-drawer") !== -1 || cls.indexOf("additional-checkout-buttons") !== -1) return false;
              return (extraExcludes || []).indexOf(el.tagName) === -1;
            });
          }
          var injectedInFooter = collectInjected(drawer.querySelector(".cart-drawer__footer"), ["FORM", "UL"]);
          var injectedInForm = collectInjected(drawer.querySelector(".cart-drawer__form"), ["BUTTON"]);

          drawer.innerHTML = fresh.innerHTML;

          var newFooter = drawer.querySelector(".cart-drawer__footer");
          if (newFooter && injectedInFooter.length) {
            injectedInFooter.reverse().forEach(function (el) { newFooter.insertBefore(el, newFooter.firstChild); });
          }
          var newForm = drawer.querySelector(".cart-drawer__form");
          if (newForm && injectedInForm.length) {
            injectedInForm.reverse().forEach(function (el) { newForm.insertBefore(el, newForm.firstChild); });
          }
          if (fresh.hasAttribute("data-item-count")) {
            drawer.setAttribute("data-item-count", fresh.getAttribute("data-item-count"));
          }
          if (fresh.hasAttribute("data-newest-product-id")) {
            drawer.setAttribute("data-newest-product-id", fresh.getAttribute("data-newest-product-id"));
          }

          var newItemsEl = drawer.querySelector("[data-cart-drawer-items]");
          if (newItemsEl) newItemsEl.scrollTop = scrollPos;

          restoreTcState();
          loadUpsells();

          drawer.classList.remove("is-loading");
          /* re-announce for apps (XE previews, Intelligems, etc.) */
          drawer.dispatchEvent(new CustomEvent("cart:rerendered", { bubbles: true }));
    }

    function refresh() {
      var url = (window.routes && window.routes.cartUrl ? window.routes.cartUrl : "/cart") + "?section_id=cart-drawer";
      fetch(url, { credentials: "same-origin", headers: { "Cache-Control": "no-cache" } })
        .then(function (res) { return res.text(); })
        .then(applyDrawerHtml)
        .catch(function () { drawer.classList.remove("is-loading"); });
    }

    function scheduleRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refresh, 60); /* dedupe the double-dispatch paths */
    }

    /* cart-perf.js hands us the drawer HTML bundled into its mutation
       response — apply it directly (no extra GET). A null payload means its
       request failed; re-fetch so the server's truth wins either way. */
    document.addEventListener("cartperf:drawer-html", function (event) {
      clearTimeout(refreshTimer); /* our apply supersedes any queued refresh */
      var html = event.detail && event.detail.html;
      if (html) { applyDrawerHtml(html); } else { refresh(); }
    });

    /* mini-cart path: non-bubbling, listen on the header cart element */
    if (headerCartEl) headerCartEl.addEventListener("cart:rerendered", scheduleRefresh);
    /* cart-page path: bubbling */
    document.addEventListener("cart:rerendered", function (event) {
      if (event.target === drawer) return; /* ignore our own re-announce */
      restoreTcState(); /* theme.js just swapped the cart-page section */
      scheduleRefresh();
    });

    /* auto-open on add-to-cart (product:added bubbles to document) */
    document.addEventListener("product:added", function () {
      drawer.classList.add("is-loading");
      open();
    });

    /* dim while theme.js talks to the cart endpoints */
    document.addEventListener("theme:loading:start", function () {
      if (drawer.getAttribute("aria-hidden") === "false") drawer.classList.add("is-loading");
    });

    /* initial upsell load (server renders the empty slot) */
    loadUpsells();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
